import { useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';

import { api } from './lib/api.ts';
import { useAuth } from './lib/auth.tsx';
import { NEXT_THEME, useTheme, type Theme } from './lib/theme.ts';
import { AdminPage } from './pages/Admin.tsx';
import { AlertsPage } from './pages/Alerts.tsx';
import { SalesPage } from './pages/Sales.tsx';
import { BuyersPage } from './pages/Buyers.tsx';
import { BillingPage } from './pages/Billing.tsx';
import { DemandPage } from './pages/Demand.tsx';
import { ItemPage } from './pages/Item.tsx';
import { LandingPage } from './pages/Landing.tsx';
import { LoginPage } from './pages/Login.tsx';
import { MyShopPage } from './pages/MyShop.tsx';
import { OnboardingPage } from './pages/Onboarding.tsx';
import { OpportunitiesPage } from './pages/Opportunities.tsx';
import { OverviewPage } from './pages/Overview.tsx';
import { PerformancePage } from './pages/Performance.tsx';
import { PricingPage } from './pages/Pricing.tsx';
import { RestockPage } from './pages/Restock.tsx';
import { SettingsPage } from './pages/Settings.tsx';

/**
 * Whether the sidebar is folded to icons.
 *
 * Remembered across sessions: someone who works in a narrow window wants it
 * folded every time, and re-folding it on each visit is the kind of small
 * friction people give up on.
 */
function useSidebarFolded(): [boolean, (folded: boolean) => void] {
  const [folded, setFolded] = useState<boolean>(() => {
    try {
      return localStorage.getItem('dca.sidebar') === 'folded';
    } catch {
      return false;
    }
  });

  const update = (next: boolean) => {
    setFolded(next);
    try {
      localStorage.setItem('dca.sidebar', next ? 'folded' : 'open');
    } catch {
      // A browser refusing storage is not a reason to refuse the toggle.
    }
  };

  return [folded, update];
}

export function App() {
  const { me, loading, logout, sees } = useAuth();
  const [theme, setTheme] = useTheme();
  const [folded, setFolded] = useSidebarFolded();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    const poll = () =>
      api.alerts
        .list(true)
        .then((result) => {
          if (!cancelled) setUnread(result.unread);
        })
        .catch(() => undefined);
    void poll();
    const timer = setInterval(poll, 60_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [me]);

  if (loading) {
    return (
      <div className="auth-shell">
        <div className="muted">Loading your dashboard...</div>
      </div>
    );
  }

  if (!me) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  const nextTheme: Theme = NEXT_THEME[theme];

  /**
   * A padlock on tabs the current plan does not include.
   *
   * The link still works - the page behind it is the upsell, and hiding it
   * entirely means nobody discovers what an upgrade buys. What is dishonest is
   * letting someone click through expecting data and finding a paywall with no
   * warning, so the tab says so up front.
   */
  const locked = (feature: string) => !me.plan.features.includes(feature as never);
  const lock = (feature: string) =>
    locked(feature) ? <span className="lock" title="Included on a higher plan">upgrade</span> : null;

  return (
    <div className={`app ${folded ? 'folded' : ''}`}>
      <nav className="sidebar">
        <div className="brand">
          <span className="brand-mark">CA</span>
          <span className="brand-word">ChestShop Analytics</span>
          <button
            className="ghost sidebar-fold"
            onClick={() => setFolded(!folded)}
            aria-expanded={!folded}
            aria-label={folded ? 'Expand the menu' : 'Collapse the menu'}
            title={folded ? 'Expand the menu' : 'Collapse the menu'}
          >
            {folded ? '»' : '«'}
          </button>
        </div>

        {/* Tabs are decided by role first. A buyer has no shop, so hiding the
            analytics is not an upsell - it is not showing them something they
            will never use. Tier gating happens inside the pages that survive.

            The links live in their own scroll region so that on a narrow screen
            they can become a horizontal strip without dragging the brand and
            the account block along with them. */}
        <div className="nav-links">
        {sees('where-to-buy') && (
          <>
            <div className="nav-section">Buying</div>
            <NavLink className="nav-link" to="/where-to-buy">
              <span className="nav-ico" aria-hidden="true">{'⌕'}</span>
              <span className="nav-word">Where to buy</span>
            </NavLink>
          </>
        )}

        {sees('my-shop') && (
          <>
            <div className="nav-section">Your shop</div>
            <NavLink className="nav-link" to="/" end>
              <span className="nav-ico" aria-hidden="true">{'◰'}</span>
              <span className="nav-word">Overview</span>
            </NavLink>
            <NavLink className="nav-link" to="/my-shop">
              <span className="nav-ico" aria-hidden="true">{'☷'}</span>
              <span className="nav-word">My shop</span>
            </NavLink>
            <NavLink className="nav-link" to="/pricing">
              <span className="nav-ico" aria-hidden="true">{'$'}</span>
              <span className="nav-word">Price vs market</span>
            </NavLink>
            <NavLink className="nav-link" to="/demand">
              <span className="nav-ico" aria-hidden="true">{'◷'}</span>
              <span className="nav-word">Demand</span>
              {lock('demand.heatmap')}
            </NavLink>
            <NavLink className="nav-link" to="/restock">
              <span className="nav-ico" aria-hidden="true">{'↻'}</span>
              <span className="nav-word">Restock</span>
              {lock('restock')}
            </NavLink>
            <NavLink className="nav-link" to="/opportunities">
              <span className="nav-ico" aria-hidden="true">{'◈'}</span>
              <span className="nav-word">Opportunities</span>
              {lock('opportunity')}
            </NavLink>
            <NavLink className="nav-link" to="/sales">
              <span className="nav-ico" aria-hidden="true">{'≡'}</span>
              <span className="nav-word">Sales records</span>
            </NavLink>
            <NavLink className="nav-link" to="/performance">
              <span className="nav-ico" aria-hidden="true">{'↗'}</span>
              <span className="nav-word">Performance</span>
            </NavLink>
          </>
        )}

        {sees('delivery') && (
          <>
            <div className="nav-section">Delivery</div>
            {/* Visible so the shape of the product is clear, but inert: the
                job flow behind it is not finished. */}
            <span className="nav-link disabled" aria-disabled="true" title="Not built yet">
              <span className="nav-ico" aria-hidden="true">{'⛟'}</span>
              <span className="nav-word">Delivery jobs</span>
              <span className="soon">soon</span>
            </span>
          </>
        )}

        <div className="nav-section">Account</div>
        {sees('alerts') && (
          <NavLink className="nav-link" to="/alerts">
            <span className="nav-ico" aria-hidden="true">{'⚑'}</span>
            <span className="nav-word">Alerts</span>
            {unread > 0 && <span className="count">{unread}</span>}
          </NavLink>
        )}
        {sees('connections') && (
          <NavLink className="nav-link" to="/connections">
            Connections
          </NavLink>
        )}
        <NavLink className="nav-link" to="/billing">
          <span className="nav-ico" aria-hidden="true">{'▤'}</span>
          <span className="nav-word">Plan &amp; billing</span>
        </NavLink>
        <NavLink className="nav-link" to="/settings">
          <span className="nav-ico" aria-hidden="true">{'⚙'}</span>
          <span className="nav-word">Settings</span>
        </NavLink>

        {me.subscriber.isAdmin && (
          <>
            <div className="nav-section">Operations</div>
            <NavLink className="nav-link" to="/admin">
              <span className="nav-ico" aria-hidden="true">{'⌘'}</span>
              <span className="nav-word">Admin console</span>
            </NavLink>
          </>
        )}

        </div>

        <div className="sidebar-foot">
          <div className="small muted sidebar-who">
            {me.subscriber.email}
            <br />
            {me.plan.name} plan
            {me.subscriber.isAdmin && (
              <>
                <br />
                <span className="badge critical" style={{ marginTop: 4 }}>
                  operator account
                </span>
              </>
            )}
          </div>
          <div className="sidebar-actions">
            <button className="ghost" onClick={() => setTheme(nextTheme)}>
              Theme: {theme}
            </button>
            <button className="ghost" onClick={logout}>
              Sign out
            </button>
          </div>
        </div>
      </nav>

      <main className="main">
        <Routes>
          <Route
            path="/"
            element={sees('my-shop') ? <OverviewPage /> : <Navigate to="/where-to-buy" replace />}
          />
          {/* Deliberately its own URL so a shop can be linked to or bookmarked. */}
          <Route path="/item/:itemKey" element={<ItemPage />} />
          <Route path="/my-shop" element={<MyShopPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/demand" element={<DemandPage />} />
          <Route path="/restock" element={<RestockPage />} />
          <Route path="/opportunities" element={<OpportunitiesPage />} />
          <Route path="/sales" element={<SalesPage />} />
          <Route path="/performance" element={<PerformancePage />} />
          <Route path="/where-to-buy" element={<BuyersPage />} />
          <Route path="/alerts" element={<AlertsPage />} />
          <Route path="/connections" element={<OnboardingPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
